import { bpsToPercent, percentToBps, toBig } from '@src/utils/number';
import type { Paths } from '@src/utils/types';
import { cloneDeep, get, isEqual, set } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isBigCompatible } from '../../../utils/big';
import type { EntityAdminClass } from '../EntityAdminClass';
import { isNotDivider } from '../components/InputsAndDropdownsDrawer/components/dividerUtils';
import { getDrawerContentRenderRule } from '../components/InputsAndDropdownsDrawer/components/registry';
import { flattenDrawerOptions } from '../components/InputsAndDropdownsDrawer/filterAndMapDrawerOptions';
import type {
  InputNumericDrawerOption,
  InputsAndDropdownsDrawerOption,
  InputsAndDropdownsDrawerOptions,
} from '../components/InputsAndDropdownsDrawer/types';
import type { EntityAdminRecord } from '../types';
import { extractFieldsFromDrawerOptions } from '../utils';

const applyNumericVariantTransformToControl = <TDrawerRecord extends EntityAdminRecord>(
  value: unknown,
  option: InputNumericDrawerOption<TDrawerRecord>
): unknown => {
  if (option.variant === 'bps') {
    return isBigCompatible(value) ? percentToBps(value) : undefined;
  }
  if (option.variant === 'percent') {
    return toBig(value)?.mul(100).toFixed();
  }
  return value;
};

const applyNumericVariantTransformToRecord = <TDrawerRecord extends EntityAdminRecord>(
  value: unknown,
  option: InputNumericDrawerOption<TDrawerRecord>
): unknown => {
  if (option.variant === 'bps') {
    return isBigCompatible(value) ? bpsToPercent(value) : value;
  }
  if (option.variant === 'percent') {
    return toBig(value)?.div(100).toFixed() ?? '';
  }
  return value;
};

const applyFieldTransformations = <TDrawerRecord extends EntityAdminRecord>(
  value: TDrawerRecord[keyof TDrawerRecord],
  drawerOption: InputsAndDropdownsDrawerOption<TDrawerRecord> | undefined
): TDrawerRecord[keyof TDrawerRecord] => {
  if (drawerOption) {
    if (isNotDivider(drawerOption) && drawerOption.transformDrawerRecordToControl) {
      return drawerOption.transformDrawerRecordToControl(value);
    }
    if (drawerOption.type === 'inputNumeric') {
      return applyNumericVariantTransformToControl(value, drawerOption) as TDrawerRecord[keyof TDrawerRecord];
    }
    const renderType = getDrawerContentRenderRule(drawerOption.type);
    if (renderType?.transformDrawerRecordToControl) {
      return renderType.transformDrawerRecordToControl(value);
    }
  }
  return value;
};

const getFieldPathDepth = (field: PropertyKey): number => String(field).split('.').length;

const sortFieldsByPathDepth = <TField extends PropertyKey>(fields: TField[]): TField[] => {
  // Overlapping drawer fields must initialize parent paths first so more specific child transforms win.
  return fields.toSorted((a, b) => getFieldPathDepth(a) - getFieldPathDepth(b));
};

const sortDrawerOptionsByPathDepth = <TDrawerOption extends { field: PropertyKey }>(
  drawerOptions: TDrawerOption[]
): TDrawerOption[] => {
  return drawerOptions.toSorted((a, b) => getFieldPathDepth(a.field) - getFieldPathDepth(b.field));
};

/**
 * Builds control-shaped form state from a record-shaped source.
 * @param entity - Record source for edit/bulk-edit/dirty-check; only drawer-option fields are included in form state.
 * @param newEntityDefault - When adding a new entity, pass defaults here (with entity omitted). The full default is
 *   cloned into form state so payload-only fields survive; drawer-option fields are transformed to control shape.
 */
const getFormState = <TDrawerRecord extends EntityAdminRecord>(
  entity: Partial<TDrawerRecord> | undefined,
  drawerOptions: InputsAndDropdownsDrawerOption<TDrawerRecord>[],
  newEntityDefault?: Partial<TDrawerRecord>
): TDrawerRecord => {
  const recordSource = entity ?? newEntityDefault;
  if (!recordSource) {
    return {} as TDrawerRecord;
  }
  const formSeed = newEntityDefault != null ? (cloneDeep(newEntityDefault) as TDrawerRecord) : ({} as TDrawerRecord);
  const fieldsWeCareAbout = sortFieldsByPathDepth(extractFieldsFromDrawerOptions(drawerOptions));
  return fieldsWeCareAbout.reduce((acc, field) => {
    const drawerOption = drawerOptions.find(option => isNotDivider(option) && option.field === field);
    const fieldValue = get(recordSource, field) as TDrawerRecord[keyof TDrawerRecord] | undefined;
    if (fieldValue != null) {
      // Clone values as they enter form state so later nested sets don't mutate the selected entity record.
      set(acc, field, cloneDeep(applyFieldTransformations(fieldValue, drawerOption)));
    }

    return acc;
  }, formSeed);
};

/**
 * Determines if a field should be skipped during validation based on visibleTabs.
 *
 * @param drawerTabs - The tabs this field belongs to (if any)
 * @param visibleTabs - The currently visible tabs (if any)
 * @returns true if validation should be skipped, false otherwise
 */
const shouldSkipFieldValidation = (drawerTabs: string[] | undefined, visibleTabs: string[] | undefined): boolean => {
  // Fields without drawerTabs are always validated
  if (!drawerTabs?.length) {
    return false;
  }

  // If visibleTabs is undefined or empty, validate all fields (prevents race condition)
  if (!visibleTabs || visibleTabs.length === 0) {
    return false;
  }

  // Skip validation only if field doesn't belong to any visible tab
  return !drawerTabs.some(tab => visibleTabs.includes(tab));
};

export const getFormErrorState = <TDrawerRecord extends EntityAdminRecord>(
  drawerOptions: InputsAndDropdownsDrawerOption<TDrawerRecord>[],
  form: TDrawerRecord,
  originalEntity: TDrawerRecord | undefined,
  visibleTabs?: string[],
  isEditing?: boolean
): Record<string, string | undefined> | null => {
  const result: Record<string, string | undefined> = {};
  drawerOptions.forEach(option => {
    if (isNotDivider(option)) {
      // [DEAL-5742] Skip error state validation for hidden fields
      if (option.getIsHidden?.(form, originalEntity)) {
        return;
      }
      if (shouldSkipFieldValidation(option.drawerTabs, visibleTabs)) {
        return;
      }

      if (option.validate) {
        // The DrawerOption has its own validation. Run this instead of the basic validation.
        // Calculate isDisabled to pass to validate function
        const isDisabled =
          (option.disabledWhenEditing && isEditing) || option.getIsDisabled?.(form, originalEntity) || false;
        const validationResult = option.validate(form, isDisabled, originalEntity);
        if (validationResult != null) {
          result[option.field] = validationResult;
        }
      } else {
        const isRequired = option.getIsRequired?.(form);
        const isPopulated = option.isValueProvided
          ? option.isValueProvided(form, option.field)
          : defaultIsValueProvided(form, option.field);

        if (isRequired && !isPopulated) {
          result[option.field] = 'This field is required';
        }
      }
    }
  });
  return Object.keys(result).length > 0 ? result : null;
};

const useEntityAdminFormValidation = <TDrawerRecord extends EntityAdminRecord>(
  drawerOptions: InputsAndDropdownsDrawerOption<TDrawerRecord>[],
  form: TDrawerRecord,
  originalEntity: TDrawerRecord | undefined,
  /**
   * The tabs that are currently visible (and will be validated) from the CustomDrawerTabs component.
   * If not provided, all fields will be validated against all tabs.
   * - this allows for validations of different scenarios of tabs
   */
  visibleTabs?: string[],
  isEditing?: boolean
) => {
  const [touchState, setTouchState] = useState<Record<string, boolean>>(() => {
    const initialTouchState: Record<string, boolean> = {};
    drawerOptions.forEach(option => {
      if (isNotDivider(option)) {
        initialTouchState[option.field] = false;
      }
    });
    return initialTouchState;
  });

  const touchAll = useCallback(() => {
    const newTouchState: Record<string, boolean> = {};
    drawerOptions.forEach(option => {
      if (isNotDivider(option)) {
        newTouchState[option.field] = true;
      }
    });
    setTouchState(newTouchState);
  }, [drawerOptions]);

  const touchField = useCallback((field: string) => {
    setTouchState(prev => ({
      ...prev,
      [field]: true,
    }));
  }, []);

  const errorState = useMemo(
    () => getFormErrorState(drawerOptions, form, originalEntity, visibleTabs, isEditing),
    [drawerOptions, form, originalEntity, visibleTabs, isEditing]
  );

  return { touchState, touchAll, touchField, errorState };
};

export type EntityAdminFormState<TDrawerRecord extends EntityAdminRecord> = {
  form: TDrawerRecord;
  handleOnFormUpdate: (field: Paths<TDrawerRecord>, value: unknown, suppressTouch?: boolean) => void;
  inheritedValues: Record<Paths<TDrawerRecord>, string | undefined>;
  someError: boolean;
  prepareFormForSave: () => TDrawerRecord;
  originalEntity: TDrawerRecord | undefined;
  errorState: Record<string, string | undefined> | null;
  touchState: Record<string, boolean>;
  touchAll: () => void;
  /** Treat the current form (or `snapshot`) as the saved baseline so leave-guards do not fire after a successful save. */
  markFormPristine: (snapshot?: TDrawerRecord) => void;
};

/**
 * Hook functionality to get the form state, update the form state, retrieve inherited values,
 * check if the form is dirty, and prepare the form for saving.
 *
 * @param param0 - The parameters for the hook
 * @returns An object containing:
 *   - formState: The current state of the form, derived from the entity and drawer options.
 *   - inheritedValues: Values inherited from the selected entities.
 *   - prepareFormForSave: A function that prepares the form data for saving by applying necessary transformations.
 */
type GetEntityAsDrawerEntity<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord> = (
  entity: TRecord,
  classEntity?: EntityAdminClass<TRecord>
) => TDrawerRecord;

type NewEntityDefault<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord> = (context?: {
  addingChildEntity?: boolean;
  entity?: TRecord;
}) => Partial<TDrawerRecord>;

export function getInitialFormState<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>({
  selectedEntities,
  flatDrawerOptions,
  getEntityAsDrawerEntity,
  newEntityDefault,
  addingChildEntity,
}: {
  selectedEntities: EntityAdminClass<TRecord>[];
  flatDrawerOptions: InputsAndDropdownsDrawerOption<TDrawerRecord>[];
  getEntityAsDrawerEntity: GetEntityAsDrawerEntity<TRecord, TDrawerRecord>;
  newEntityDefault?: NewEntityDefault<TRecord, TDrawerRecord>;
  addingChildEntity: boolean;
}): TDrawerRecord {
  const firstEntity = selectedEntities.at(0)?.data;
  const entityIDField = selectedEntities.at(0)?.entityIDField;

  if (firstEntity == null) {
    return getFormState(undefined, flatDrawerOptions, newEntityDefault?.());
  }
  if (addingChildEntity) {
    const childDefault = cloneDeep<Partial<TDrawerRecord>>(
      newEntityDefault?.({ addingChildEntity: true, entity: firstEntity }) ?? {}
    );
    if (entityIDField != null) {
      set(childDefault, entityIDField, get(firstEntity, entityIDField));
    }
    return getFormState(undefined, flatDrawerOptions, childDefault);
  }

  const fieldsWeCareAbout = sortFieldsByPathDepth(extractFieldsFromDrawerOptions(flatDrawerOptions));
  const drawerEntities = selectedEntities.map(entity => getEntityAsDrawerEntity(entity.data, entity));
  const firstDrawerEntity = drawerEntities.at(0);
  // Loop through each field and test if all values are the same from every selected entity
  const bulkEditFormInitialState = fieldsWeCareAbout.reduce((acc, field) => {
    const firstValue = get(firstDrawerEntity, field);
    const allValuesAreSame = drawerEntities.every(entity => isEqual(get(entity, field), firstValue));
    if (allValuesAreSame) {
      set(acc, field, cloneDeep(firstValue));
    }
    return acc;
  }, {} as TDrawerRecord);
  return getFormState(bulkEditFormInitialState, flatDrawerOptions);
}

export function prepareFormStateForSave<TDrawerRecord extends EntityAdminRecord>(
  form: TDrawerRecord,
  flatDrawerOptions: InputsAndDropdownsDrawerOption<TDrawerRecord>[]
): TDrawerRecord {
  // Overlapping drawer paths must save parent paths first so more specific child transforms win.
  const sortedOptions = sortDrawerOptionsByPathDepth(flatDrawerOptions.filter(isNotDivider));

  return sortedOptions.reduce((acc, option) => {
    const formField = get(form, option.field);
    if (formField != null) {
      const renderType = getDrawerContentRenderRule<TDrawerRecord>(option.type);

      // Transform the form control values back to the drawer record
      if (option.transformControlToDrawerRecord) {
        set(acc, option.field, option.transformControlToDrawerRecord(formField));
      } else if (option.type === 'inputNumeric') {
        set(acc, option.field, applyNumericVariantTransformToRecord(formField, option));
      } else if (renderType?.transformControlToDrawerRecord) {
        set(acc, option.field, renderType.transformControlToDrawerRecord(formField));
      }
    }
    return acc;
  }, cloneDeep(form));
}

export function useEntityAdminFormState<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>({
  selectedEntities,
  drawerOptions,
  getEntityAsDrawerEntity,
  setIsEntityAdminDrawerDirty,
  newEntityDefault,
  visibleTabs,
  isEditing,
  addingChildEntity = false,
}: {
  selectedEntities: EntityAdminClass<TRecord>[];
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>;
  getEntityAsDrawerEntity: GetEntityAsDrawerEntity<TRecord, TDrawerRecord>;
  setIsEntityAdminDrawerDirty: (dirty: boolean) => void;
  newEntityDefault?: NewEntityDefault<TRecord, TDrawerRecord>;
  visibleTabs?: string[];
  isEditing?: boolean;
  addingChildEntity?: boolean;
}): EntityAdminFormState<TDrawerRecord> {
  const flatDrawerOptions = useMemo(() => flattenDrawerOptions(drawerOptions), [drawerOptions]);
  const firstEntity = selectedEntities.at(0)?.data;
  const entityIDField = selectedEntities.at(0)?.entityIDField;

  const [form, setForm] = useState<TDrawerRecord>(() =>
    getInitialFormState({
      selectedEntities,
      flatDrawerOptions,
      getEntityAsDrawerEntity,
      newEntityDefault,
      addingChildEntity,
    })
  );
  const initialFormRef = useRef(form);
  const savedBaselineRef = useRef<TDrawerRecord | undefined>(undefined);
  const entityKey = firstEntity == null || entityIDField == null ? undefined : get(firstEntity, entityIDField);

  useEffect(() => {
    savedBaselineRef.current = undefined;
  }, [entityKey]);

  const getCollectivePlaceholderLabel = useCallback(
    (drawerOption: InputsAndDropdownsDrawerOption<TDrawerRecord>): string | undefined => {
      if (!isNotDivider(drawerOption)) {
        return undefined;
      }
      if (addingChildEntity) {
        const parent = selectedEntities.at(0);
        if (parent == null || String(drawerOption.field) === String(parent.entityIDField)) {
          return undefined;
        }
        const parentValue = get(parent.data, drawerOption.field) as TDrawerRecord[keyof TDrawerRecord] | undefined;
        return parentValue != null ? applyFieldTransformations(parentValue, drawerOption) : undefined;
      }
      const parentValue = selectedEntities.every(entity => entity.isFieldInherited(drawerOption.field))
        ? (get(selectedEntities.at(0), drawerOption.field) as TDrawerRecord[keyof TDrawerRecord])
        : undefined;

      // [DEAL-5278] We can inherit "false" boolean values from the parent entity
      return parentValue != null ? applyFieldTransformations(parentValue, drawerOption) : undefined;
    },
    [addingChildEntity, selectedEntities]
  );

  const inheritedValues = useMemo(() => {
    const values: Record<Paths<TDrawerRecord>, string | undefined> = {} as Record<
      Paths<TDrawerRecord>,
      string | undefined
    >;
    flatDrawerOptions.forEach(option => {
      if (isNotDivider(option)) {
        values[option.field] = getCollectivePlaceholderLabel(option);
      }
    });
    return values;
  }, [flatDrawerOptions, getCollectivePlaceholderLabel]);

  const prepareFormForSave = useCallback(
    () => prepareFormStateForSave(form, flatDrawerOptions),
    [flatDrawerOptions, form]
  );

  useEffect(() => {
    if (firstEntity == null || addingChildEntity) {
      // New entity and add-child: compare against the seeded form so leave-guards fire after edits.
      setIsEntityAdminDrawerDirty(!isEqual(form, initialFormRef.current));
    } else {
      const firstSelectedEntity = selectedEntities.at(0);
      const drawerEntity = getEntityAsDrawerEntity(firstEntity, firstSelectedEntity);
      const baseline = savedBaselineRef.current ?? getFormState(drawerEntity, flatDrawerOptions);
      setIsEntityAdminDrawerDirty(!isEqual(form, baseline));
    }
  }, [
    addingChildEntity,
    flatDrawerOptions,
    firstEntity,
    form,
    getEntityAsDrawerEntity,
    setIsEntityAdminDrawerDirty,
    selectedEntities,
  ]);

  const originalEntity = useMemo(() => {
    if (firstEntity == null || addingChildEntity) {
      return undefined;
    }
    const firstSelectedEntity = selectedEntities.at(0);
    return getEntityAsDrawerEntity(firstEntity, firstSelectedEntity);
  }, [addingChildEntity, firstEntity, getEntityAsDrawerEntity, selectedEntities]);

  const { touchAll, touchField, errorState, touchState } = useEntityAdminFormValidation(
    flatDrawerOptions,
    form,
    originalEntity,
    visibleTabs,
    isEditing
  );

  const handleOnFormUpdate = useCallback(
    (field: Paths<TDrawerRecord>, value: unknown, suppressTouch = false) => {
      if (!suppressTouch) {
        touchField(field);
      }
      setForm(prev => {
        const next = cloneDeep(prev);
        set(next, field, value);
        return next;
      });
    },
    [touchField]
  );

  const markFormPristine = useCallback(
    (snapshot?: TDrawerRecord) => {
      const next = cloneDeep(snapshot ?? form);
      savedBaselineRef.current = next;
      initialFormRef.current = next;
      if (snapshot != null) {
        setForm(next);
      }
      setIsEntityAdminDrawerDirty(false);
    },
    [form, setIsEntityAdminDrawerDirty]
  );

  return {
    form,
    handleOnFormUpdate,
    inheritedValues,
    someError: errorState != null, // errorState is returned as null if there are no entries in it
    prepareFormForSave,
    originalEntity,
    touchState,
    errorState,
    touchAll,
    markFormPristine,
  };
}

function defaultIsValueProvided<TDrawerRecord extends EntityAdminRecord>(form: TDrawerRecord, formField: string) {
  const value = get(form, formField);
  const isEmpty = value == null || value === '' || (Array.isArray(value) && value.length === 0);
  return !isEmpty;
}
